<script setup lang="ts">
import { PriceOverrideStatus } from "@classes-service-types/index";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";
import DashboardCard from "@/components/MyStudents/Display/DashboardCard.vue";
import { useFilters } from "@/components/OperationsApp/composables/useFilters.ts";
import { useProductTypeReasonFilters } from "@/components/OperationsApp/composables/useProductTypeReasonFilters.ts";
import { useQFilter } from "@/components/OperationsApp/composables/useQFilter.ts";
import { useUpdateDecisionDates } from "@/components/OperationsApp/composables/useUpdateDecisionDates.ts";
import { REJECTED_DISPLAY_TEXT } from "@/components/OperationsApp/constants.ts";
import { useGetPriceOverridesCount } from "@/providers/priceOverrides/useGetPriceOverridesCount.ts";
import { ALL_PRICE_OVERRIDE_STATUSES_SORTED } from "./constants.ts";

defineOptions({
  name: "PriceOverrideHeader",
});

const route = useRoute();
const toast = useToast();

const { session, city, isAllStatus, orderStatus } = useFilters();
const { libraryId, reasonId } = useProductTypeReasonFilters();
const { q } = useQFilter();
const { decisionDateEnd, decisionDateStart } = useUpdateDecisionDates();

const { isLoading, data: priceOverridesCountResponse } = useGetPriceOverridesCount(
  undefined,
  session,
  city,
  libraryId,
  reasonId,
  orderStatus,
  decisionDateStart,
  decisionDateEnd,
  q,
  true,
  {
    refetchInterval: false,
    throwOnError: (error) => {
      toast.add({
        severity: "error",
        summary: "Price Overrides",
        detail: `Failed to get Price Overrides please refresh page. ${error.message}`,
        group: "br-counts",
        life: 5000,
      });
      return false;
    },
  },
);

const totalCount = computed(
  () =>
    (priceOverridesCountResponse?.value?.data?.data?.needs_review || 0) +
    (priceOverridesCountResponse?.value?.data?.data?.approved || 0) +
    (priceOverridesCountResponse?.value?.data?.data?.rejected || 0) +
    (priceOverridesCountResponse?.value?.data?.data?.reverted || 0) +
    (priceOverridesCountResponse?.value?.data?.data?.canceled || 0),
);
</script>


<template>
  <div class="grid grid-cols-5 gap-8">
    <Toast position="bottom-right" group="br-counts" />
    <router-link :to="{ name: 'price-override-approvals', query: { ...route.query, status: PriceOverrideStatus.NeedsReview } }">
      <dashboard-card
        :number="priceOverridesCountResponse?.data?.data?.needs_review || 0"
        :description="PriceOverrideStatus.NeedsReview"
        :class-format="`${
                route.query.status === PriceOverrideStatus.NeedsReview ? 'bg-blue-400' : 'bg-white'
              } min-w-7 shadow-lg rounded-lg p-4 px-6`"
        data-test-id="price-overrides-pending-card"
        mui-icon-name="rate_review"
        mui-icon-class="text-university-tertiary"
        :loading="isLoading"
      />
    </router-link>
    <router-link :to="{ name: 'price-override-approvals', query: { ...route.query, status: PriceOverrideStatus.Approved } }">
      <dashboard-card
        :number="priceOverridesCountResponse?.data?.data?.approved || 0"
        :description="PriceOverrideStatus.Approved"
        :class-format="`${
                route.query.status === PriceOverrideStatus.Approved ? 'bg-blue-400' : 'bg-white'
              } min-w-6 shadow-lg rounded-lg p-4 px-6`"
        data-test-id="price-overrides-approved-card"
        mui-icon-name="check_circle"
        mui-icon-class="text-green-bright"
        :loading="isLoading"
      />
    </router-link>
    <router-link :to="{ name: 'price-override-approvals', query: { ...route.query, status: PriceOverrideStatus.Rejected } }">
      <dashboard-card
        :number="priceOverridesCountResponse?.data?.data?.rejected || 0"
        :description="REJECTED_DISPLAY_TEXT"
        :class-format="`${
                route.query.status === PriceOverrideStatus.Rejected ? 'bg-blue-400' : 'bg-white'
              } min-w-6 shadow-lg rounded-lg p-4 px-6`"
        data-test-id="price-overrides-declined-card"
        mui-icon-name="unpublished"
        mui-icon-class="text-red-700"
        :loading="isLoading"
      />
    </router-link>
    <router-link :to="{ name: 'price-override-approvals', query: { ...route.query, status: PriceOverrideStatus.Reverted } }">
      <dashboard-card
        :number="priceOverridesCountResponse?.data?.data?.reverted || 0"
        :description="PriceOverrideStatus.Reverted"
        :class-format="`${
                route.query.status === PriceOverrideStatus.Reverted ? 'bg-blue-400' : 'bg-white'
              } min-w-6 shadow-lg rounded-lg p-4 px-6`"
        data-test-id="price-overrides-reverted-card"
        mui-icon-name="unpublished"
        mui-icon-class="text-red-700"
        :loading="isLoading"
      />
    </router-link>
    <router-link :to="{ name: 'price-override-approvals', query: { ...route.query, status: ALL_PRICE_OVERRIDE_STATUSES_SORTED } }">
      <dashboard-card
        :number="totalCount"
        description="All"
        :class-format="`${
                isAllStatus ? 'bg-blue-400' : 'bg-white'
              } min-w-6 shadow-lg rounded-lg p-4 px-6`"
        data-test-id="price-overrides-all-card"
        mui-icon-name="all_inclusive"
        :loading="isLoading"
      />
    </router-link>
  </div>
</template>

<style scoped>
</style>
